import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "@core/theme/tokens";
import { ApiError } from "@core/http";
import { Icon } from "@core/icons";
import { recoverPassword } from "./api";
import { AccessLayout } from "./AccessLayout";
import { BigButton, Field, LinkButton, Notice } from "./primitives";

// SPEC-221 §4: un solo campo de correo, misma respuesta exista o no la
// cuenta — el estado de éxito no depende de lo que devuelva el backend más
// allá de `validityHours`.
export function RecoverPasswordScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState<{ validityHours: number } | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.includes("@")) {
      setError("Ingresa un correo válido.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const result = await recoverPassword(email);
      setSent(result);
    } catch (err) {
      // SPEC-221 §4: la respuesta es siempre la misma exista o no la
      // cuenta — pero esa no-revelación ocurre en el 200 del backend
      // (el contrato no define un status distinto para cuenta inexistente),
      // no acá. Un error real (400 de formato, 5xx o falla de red) sí debe
      // distinguirse: tratarlo como éxito silencioso deja al usuario
      // esperando un correo que nunca se envió.
      if (err instanceof ApiError && err.status === 400) {
        setError("Ingresa un correo válido.");
      } else {
        setError("No se pudo procesar la solicitud. Intenta de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AccessLayout sub="Recuperar contraseña">
      <LinkButton
        onClick={() => {
          navigate("/access");
        }}
        style={{ margin: "-8px 0 24px", paddingLeft: 0 }}
      >
        ←&nbsp;&nbsp;Volver
      </LinkButton>

      {sent ? (
        <div>
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: tokens.gold.dim,
              border: `1px solid ${tokens.gold.line}`,
              margin: "8px auto 20px",
            }}
          >
            <Icon
              name="check"
              size={27}
              color={tokens.gold.light}
              stroke={2.2}
            />
          </div>
          <h1
            style={{
              font: `300 27px/1.2 ${tokens.font.display}`,
              color: tokens.text.primary,
              margin: "0 0 12px",
              textAlign: "center",
            }}
          >
            Revisa tu correo
          </h1>
          <p
            style={{
              font: `400 14px/1.6 ${tokens.font.ui}`,
              color: tokens.text.secondary,
              margin: "0 0 8px",
              textAlign: "center",
            }}
          >
            Enviamos un enlace a{" "}
            <b style={{ color: tokens.text.primary }}>{email}</b>.
            {sent.validityHours > 0
              ? ` Es válido por ${String(sent.validityHours)} horas y sirve una sola vez.`
              : " Sirve una sola vez."}
          </p>
          <BigButton
            tone="ghost"
            onClick={() => {
              setSent(null);
            }}
            style={{ marginTop: 20 }}
          >
            Reenviar el enlace
          </BigButton>
        </div>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)}>
          <h1
            style={{
              font: `300 27px/1.2 ${tokens.font.display}`,
              color: tokens.text.primary,
              margin: "8px 0 12px",
            }}
          >
            Recuperar contraseña
          </h1>
          <p
            style={{
              font: `400 14px/1.6 ${tokens.font.ui}`,
              color: tokens.text.secondary,
              margin: "0 0 24px",
            }}
          >
            Escribe el correo con el que fue registrado tu usuario del portal.
            Te enviaremos un enlace para crear una nueva contraseña.
          </p>
          {error ? <Notice>{error}</Notice> : null}
          <Field
            label="Correo"
            type="email"
            value={email}
            onChange={(value) => {
              setEmail(value);
              setError(null);
            }}
            placeholder="nombre@starcircle.com"
            error={Boolean(error)}
          />
          <BigButton type="submit" disabled={submitting}>
            {submitting ? "Enviando…" : "Enviar enlace"}
          </BigButton>
        </form>
      )}
    </AccessLayout>
  );
}
